import { emptyValueForSchema, getSchemaAt, CASE_TYPE_SCHEMA } from "../../../../case-spec";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrayShell,
  BubbleListEditor,
  EnumSelect,
  RowCard,
  TextField,
  useOpenIndex,
} from "../shared-ui";
import {
  asArray,
  enumOptionsAt,
  isRecord,
  str,
  type PanelProps,
} from "../shared";
import {
  appendAtPath,
  childPath,
  getAtPath,
  setAtPath,
} from "../path-utils";

const PATH = "game.guideSteps";

export function GuideStepsPanel({ root, disabled, onChangeRoot }: PanelProps) {
  const steps = asArray(getAtPath(root, PATH));
  const { openIndex, setOpenIndex, toggle } = useOpenIndex(steps.length);
  const itemSchema = getSchemaAt(CASE_TYPE_SCHEMA, "game.guideSteps[0]");

  return (
    <ArrayShell
      title="引导步骤"
      count={steps.length}
      disabled={disabled}
      onAdd={() => {
        const row = itemSchema
          ? emptyValueForSchema(itemSchema)
          : { kind: "guide", messages: [{ body: "" }] };
        const rec: Record<string, unknown> = isRecord(row)
          ? { ...row }
          : { kind: "guide" };
        if (!rec.kind) rec.kind = "guide";
        if (rec.kind === "guide" && !Array.isArray(rec.messages)) {
          rec.messages = [{ body: "" }];
        }
        onChangeRoot(appendAtPath(root, PATH, rec));
        setOpenIndex(steps.length);
      }}
    >
      <ul className="flex flex-col gap-2">
        {steps.map((step, index) => {
          const rec = isRecord(step) ? step : {};
          const kind = str(rec.kind, "guide") === "choice" ? "choice" : "guide";
          const itemPath = childPath(PATH, index);
          const preview =
            kind === "choice"
              ? `选择题 · 步骤 ${index + 1}`
              : str(
                  asArray(rec.messages).find((m) => isRecord(m) && str(m.body))
                    ? (asArray(rec.messages).find(
                        (m) => isRecord(m) && str(m.body),
                      ) as { body?: string })?.body
                    : "",
                ) || `旁白 · 步骤 ${index + 1}`;

          return (
            <RowCard
              key={itemPath}
              title={
                preview.length > 56 ? `${preview.slice(0, 56)}…` : preview
              }
              open={openIndex === index}
              onToggle={() => toggle(index)}
              disabled={disabled}
              index={index}
              total={steps.length}
              arrayPath={PATH}
              root={root}
              onChangeRoot={onChangeRoot}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={kind === "choice" ? "info" : "secondary"}>
                  {kind === "choice" ? "选择" : "旁白"}
                </Badge>
              </div>
              <EnumSelect
                label="类型"
                fieldKey="kind"
                value={kind}
                options={enumOptionsAt("game.guideSteps[0].kind")}
                disabled={disabled}
                onChange={(v) => {
                  const nextKind = v === "choice" ? "choice" : "guide";
                  let next = setAtPath(
                    root,
                    childPath(itemPath, "kind"),
                    nextKind,
                  );
                  if (nextKind === "choice") {
                    const choices = asArray(getAtPath(next, childPath(itemPath, "choices")));
                    if (choices.length < 2) {
                      next = setAtPath(next, childPath(itemPath, "choices"), [
                        { label: "", reply: [{ body: "" }] },
                        { label: "", reply: [{ body: "" }] },
                      ]);
                    }
                  } else {
                    const messages = asArray(
                      getAtPath(next, childPath(itemPath, "messages")),
                    );
                    if (messages.length === 0) {
                      next = setAtPath(next, childPath(itemPath, "messages"), [
                        { body: "" },
                      ]);
                    }
                  }
                  onChangeRoot(next);
                }}
              />

              {kind === "guide" ? (
                <BubbleListEditor
                  root={root}
                  path={childPath(itemPath, "messages")}
                  label="向导气泡"
                  disabled={disabled}
                  onChangeRoot={onChangeRoot}
                />
              ) : (
                <ChoicesEditor
                  root={root}
                  itemPath={itemPath}
                  disabled={disabled}
                  onChangeRoot={onChangeRoot}
                />
              )}

              <BubbleListEditor
                root={root}
                path={childPath(itemPath, "after")}
                label="后续气泡（可选）"
                disabled={disabled}
                onChangeRoot={onChangeRoot}
              />
            </RowCard>
          );
        })}
      </ul>
    </ArrayShell>
  );
}

function ChoicesEditor({
  root,
  itemPath,
  disabled,
  onChangeRoot,
}: {
  root: unknown;
  itemPath: string;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
}) {
  const choicesPath = childPath(itemPath, "choices");
  let choices = asArray(getAtPath(root, choicesPath));
  // pad to 2 for UX
  while (choices.length < 2) {
    choices = [...choices, { label: "", reply: [{ body: "" }] }];
  }
  choices = choices.slice(0, 2);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-medium">选项（固定 2 个）</p>
      {choices.map((choice, i) => {
        const rec = isRecord(choice) ? choice : {};
        const cPath = childPath(choicesPath, i);
        return (
          <div
            key={i}
            className="flex flex-col gap-2 rounded-md border border-border/70 p-2"
          >
            <TextField
              label={`选项 ${i + 1} 文案`}
              value={str(rec.label)}
              disabled={disabled}
              onChange={(v) =>
                onChangeRoot(setAtPath(root, childPath(cPath, "label"), v))
              }
            />
            <BubbleListEditor
              root={root}
              path={childPath(cPath, "reply")}
              label="选择后向导回复"
              disabled={disabled}
              onChangeRoot={onChangeRoot}
            />
          </div>
        );
      })}
      {asArray(getAtPath(root, choicesPath)).length !== 2 ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onChangeRoot(setAtPath(root, choicesPath, choices))}
        >
          规范为 2 个选项
        </Button>
      ) : null}
    </div>
  );
}
